import {settingsService} from '../services/settingsService'

/**
 * Service for managing client identification
 */
export class ClientIdService {
  private static readonly STORAGE_KEY = 'client_id'
  private clientId: string | null = null

  /**
   * Generates a new unique client ID
   */
  private static generateNewId(): string {
    return crypto.randomUUID ? crypto.randomUUID() :
      Date.now().toString(36) + Math.random().toString(36).substring(2)
  }

  /**
   * Stores the client ID in local storage
   */
  private static storeId(id: string): void {
    localStorage.setItem(ClientIdService.STORAGE_KEY, id)
  }

  /**
   * Retrieves the client ID from local storage
   */
  private static getStoredId(): string | null {
    return localStorage.getItem(ClientIdService.STORAGE_KEY)
  }

  /**
   * Gets the current client ID without generating a new one
   * Returns null if no client ID exists
   */
  public getClientId(): string | null {
    if (this.clientId) {
      return this.clientId
    }

    const storedId = ClientIdService.getStoredId()
    if (storedId) {
      this.clientId = storedId
      return this.clientId
    }

    return null
  }

  /**
   * Checks if a client ID exists
   * Returns true if a client ID exists, false otherwise
   */
  public hasClientId(): boolean {
    return this.getClientId() !== null
  }

  /**
   * Generates a new client ID, stores it, and sends it to the server
   * Returns the newly generated ID
   */
  public generateClientId(): string {
    const newId = ClientIdService.generateNewId()
    ClientIdService.storeId(newId)
    this.clientId = newId

    const payload = {
      clientId: newId,
    }

    settingsService.postClientId(payload).then(() => {
      console.log('Client ID sent to server')
    })

    return newId
  }

  /**
   * Resets the client ID
   */
  public resetClientId(): void {
    ClientIdService.storeId('')
    this.clientId = null
  }
}

// Export a singleton instance
export const clientIdService = new ClientIdService()

export const getClientId = () => clientIdService.getClientId()
export const hasClientId = () => clientIdService.hasClientId()
export const generateClientId = () => clientIdService.generateClientId()
export const resetClientId = () => clientIdService.resetClientId()
